import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import Spinner from '../../../components/Spinner';
import Modal from '../../../components/Modal';
import TenantService, { TenantFormPayload } from '../../../services/tenant.service';
import ProductService from '../../../services/product.service';
import PlanService from '../../../services/plan.service';
import MasterLookupService from '../../../services/master-lookup.service';
import { Modules, RouteParamsTypes } from '../../../enums';

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface PlanOption {
  id: number;
  plan_code: string;
  plan_name: string;
}

interface LookupOption {
  id: number;
  [key: string]: unknown;
}

interface TenantFormState {
  tenant_code: string;
  tenant_name: string;
  slug: string;
  product_id: number | '';
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  country_id: number | '';
  timezone_id: number | '';
  category_id: number | '';
  isolation_mode: 'shared' | 'dedicated';
  plan_id: number | '';
  subscription_start_date: string;
  address_notes: string;
}

const today = new Date().toISOString().slice(0, 10);

const initialForm: TenantFormState = {
  tenant_code: '',
  tenant_name: '',
  slug: '',
  product_id: '',
  owner_name: '',
  owner_email: '',
  owner_phone: '',
  country_id: '',
  timezone_id: '',
  category_id: '',
  isolation_mode: 'shared',
  plan_id: '',
  subscription_start_date: today,
  address_notes: '',
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const TenantForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<TenantFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const slugManuallyEdited = useRef(false);
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [products, setProducts] = useState<ProductOption[]>([]);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [countries, setCountries] = useState<LookupOption[]>([]);
  const [timezones, setTimezones] = useState<LookupOption[]>([]);
  const [categories, setCategories] = useState<LookupOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'countries', includeDeleted: false, limit: 200 }).then((response: any) => {
      setCountries(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'timezones', includeDeleted: false, limit: 200 }).then((response: any) => {
      setTimezones(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'categories', includeDeleted: false, limit: 200 }).then((response: any) => {
      setCategories(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    TenantService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          tenant_code: row.tenant_code ?? '',
          tenant_name: row.tenant_name ?? '',
          slug: row.slug ?? '',
          product_id: row.product_id ?? '',
          owner_name: row.owner_name ?? '',
          owner_email: row.owner_email ?? '',
          owner_phone: row.owner_phone ?? '',
          country_id: row.country_id ?? '',
          timezone_id: row.timezone_id ?? '',
          category_id: row.category_id ?? '',
          isolation_mode: row.isolation_mode ?? 'shared',
          plan_id: row.plan_id ?? '',
          subscription_start_date: row.subscription_start_date ? String(row.subscription_start_date).slice(0, 10) : today,
          address_notes: row.address_notes ?? '',
        });
        slugManuallyEdited.current = true;
      })
      .finally(() => setFetching(false));
  }, [id]);

  // Product -> Plan re-filtering cascade (US-022 AC-3)
  useEffect(() => {
    if (!formData.product_id) {
      setPlans([]);
      return;
    }
    setPlansLoading(true);
    PlanService.list({ productId: Number(formData.product_id), includeDeleted: false, limit: 100 })
      .then((response: any) => setPlans(response?.data?.items ?? []))
      .finally(() => setPlansLoading(false));
  }, [formData.product_id]);

  useEffect(() => {
    return () => {
      if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    };
  }, []);

  // Browser-level unsaved-changes guard (no data router in this app, so useBlocker isn't available)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const markDirty = useCallback((updater: (prev: TenantFormState) => TenantFormState) => {
    setIsDirty(true);
    setFormData(updater);
  }, []);

  const handleChange = useCallback(
    (e: any, name: string) => {
      const numericFields = ['product_id', 'country_id', 'timezone_id', 'category_id', 'plan_id'];
      const value = numericFields.includes(name) ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;

      if (name === 'product_id') {
        markDirty((prev) => ({ ...prev, product_id: value, plan_id: '' }));
        return;
      }

      if (name === 'tenant_name') {
        markDirty((prev) => ({
          ...prev,
          tenant_name: value,
          slug: slugManuallyEdited.current ? prev.slug : slugify(value),
        }));
        return;
      }

      if (name === 'slug') {
        slugManuallyEdited.current = true;
        setSlugStatus('idle');
      }

      markDirty((prev) => ({ ...prev, [name]: value }));
    },
    [markDirty]
  );

  const checkSlug = useCallback(() => {
    const slug = formData.slug.trim();
    if (!slug) return;
    setSlugStatus('checking');
    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    slugDebounceRef.current = setTimeout(() => {
      TenantService.checkSlug(slug, edit ? id : undefined)
        .then((response: any) => {
          setSlugStatus(response?.data?.available ? 'available' : 'taken');
        })
        .catch(() => setSlugStatus('idle'));
    }, 400);
  }, [formData.slug, edit, id]);

  const validate = useCallback(
    (data: TenantFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.tenant_name.trim()) nextErrors.tenant_name = t('tenants.form.pageTexts.fields.tenantName.errors.required');
      if (!data.slug.trim()) nextErrors.slug = t('tenants.form.pageTexts.fields.slug.errors.required');
      if (!data.product_id) nextErrors.product_id = t('tenants.form.pageTexts.fields.product.errors.required');
      if (!data.owner_name.trim()) nextErrors.owner_name = t('tenants.form.pageTexts.fields.ownerName.errors.required');
      if (!data.owner_email.trim()) nextErrors.owner_email = t('tenants.form.pageTexts.fields.ownerEmail.errors.required');
      if (!data.country_id) nextErrors.country_id = t('tenants.form.pageTexts.fields.country.errors.required');
      if (!data.plan_id) nextErrors.plan_id = t('tenants.form.pageTexts.fields.plan.errors.required');
      if (!data.subscription_start_date) nextErrors.subscription_start_date = t('tenants.form.pageTexts.fields.startDate.errors.required');
      if (slugStatus === 'taken') nextErrors.slug = t('tenants.form.pageTexts.fields.slug.errors.taken');
      return nextErrors;
    },
    [t, slugStatus]
  );

  const goToList = useCallback(() => navigate(`${Modules.tenants}${RouteParamsTypes.blank}`), [navigate]);

  const handleCancelClick = useCallback(() => {
    if (isDirty) {
      setShowLeaveConfirm(true);
      return;
    }
    goToList();
  }, [isDirty, goToList]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: TenantFormPayload = {
          tenant_name: formData.tenant_name,
          slug: formData.slug,
          product_id: Number(formData.product_id),
          owner_name: formData.owner_name,
          owner_email: formData.owner_email,
          owner_phone: formData.owner_phone || undefined,
          country_id: Number(formData.country_id),
          timezone_id: formData.timezone_id ? Number(formData.timezone_id) : undefined,
          category_id: formData.category_id ? Number(formData.category_id) : undefined,
          isolation_mode: formData.isolation_mode,
          plan_id: Number(formData.plan_id),
          subscription_start_date: formData.subscription_start_date,
          address_notes: formData.address_notes || undefined,
        };

        if (edit && id) {
          await TenantService.update(id, payload);
          toast.success(<Toast message={t('tenants.form.pageTexts.editSuccess')} />, { icon: false });
          setIsDirty(false);
          navigate(`${Modules.tenants}/${id}`);
        } else {
          const response: any = await TenantService.create(payload);
          toast.success(<Toast message={t('tenants.form.pageTexts.addSuccess')} />, { icon: false });
          setIsDirty(false);
          navigate(`${Modules.tenants}/${response?.data?.id}`);
        }
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  const slugHelperText = useMemo(() => {
    if (slugStatus === 'checking') return t('tenants.form.pageTexts.fields.slug.checking');
    if (slugStatus === 'available') return t('tenants.form.pageTexts.fields.slug.available');
    if (slugStatus === 'taken') return t('tenants.form.pageTexts.fields.slug.taken');
    return '';
  }, [slugStatus, t]);

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('tenants.form.pageTexts.editTitle') : t('tenants.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        {edit && (
          <div className="col-md-3">
            <Input id="tenant_code" name="tenant_code" label={t('tenants.form.pageTexts.fields.tenantCode.label')} value={formData.tenant_code} disabled onChange={() => {}} />
          </div>
        )}
        <div className="col-md-4">
          <Input
            id="tenant_name"
            name="tenant_name"
            label={t('tenants.form.pageTexts.fields.tenantName.label')}
            required
            value={formData.tenant_name}
            error={!!errors.tenant_name}
            errorMessage={errors.tenant_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-5">
          <Input
            id="slug"
            name="slug"
            label={t('tenants.form.pageTexts.fields.slug.label')}
            required
            value={formData.slug}
            error={!!errors.slug || slugStatus === 'taken'}
            errorMessage={errors.slug}
            onChange={handleChange}
            onBlur={checkSlug}
          />
          {slugHelperText && (
            <small className={slugStatus === 'available' ? 'text-success' : slugStatus === 'taken' ? 'text-danger' : 'text-muted'}>
              {slugHelperText}
            </small>
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="product_id">
            {t('tenants.form.pageTexts.fields.product.label')}
          </label>
          <select
            id="product_id"
            name="product_id"
            className={`form-select ${errors.product_id ? 'is-invalid' : ''}`}
            value={formData.product_id}
            onChange={(e) => handleChange(e, 'product_id')}
          >
            <option value="">{t('tenants.form.pageTexts.fields.product.placeholder')}</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.product_code} — {product.product_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="plan_id">
            {t('tenants.form.pageTexts.fields.plan.label')}
          </label>
          <select
            id="plan_id"
            name="plan_id"
            className={`form-select ${errors.plan_id ? 'is-invalid' : ''}`}
            value={formData.plan_id}
            disabled={!formData.product_id || plansLoading}
            onChange={(e) => handleChange(e, 'plan_id')}
          >
            <option value="">
              {plansLoading ? t('tenants.form.pageTexts.fields.plan.loading') : t('tenants.form.pageTexts.fields.plan.placeholder')}
            </option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.plan_code} — {plan.plan_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="isolation_mode">
            {t('tenants.form.pageTexts.fields.isolationMode.label')}
          </label>
          <select
            id="isolation_mode"
            name="isolation_mode"
            className="form-select"
            value={formData.isolation_mode}
            onChange={(e) => handleChange(e, 'isolation_mode')}
          >
            <option value="shared">{t('tenants.form.pageTexts.fields.isolationMode.shared')}</option>
            <option value="dedicated">{t('tenants.form.pageTexts.fields.isolationMode.dedicated')}</option>
          </select>
        </div>

        <div className="col-md-4">
          <Input
            id="owner_name"
            name="owner_name"
            label={t('tenants.form.pageTexts.fields.ownerName.label')}
            required
            value={formData.owner_name}
            error={!!errors.owner_name}
            errorMessage={errors.owner_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="owner_email"
            name="owner_email"
            type="email"
            label={t('tenants.form.pageTexts.fields.ownerEmail.label')}
            required
            value={formData.owner_email}
            error={!!errors.owner_email}
            errorMessage={errors.owner_email}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="owner_phone"
            name="owner_phone"
            label={t('tenants.form.pageTexts.fields.ownerPhone.label')}
            value={formData.owner_phone}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="country_id">
            {t('tenants.form.pageTexts.fields.country.label')}
          </label>
          <select
            id="country_id"
            name="country_id"
            className={`form-select ${errors.country_id ? 'is-invalid' : ''}`}
            value={formData.country_id}
            onChange={(e) => handleChange(e, 'country_id')}
          >
            <option value="">{t('tenants.form.pageTexts.fields.country.placeholder')}</option>
            {countries.map((country: any) => (
              <option key={country.id} value={country.id}>
                {country.country_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="timezone_id">
            {t('tenants.form.pageTexts.fields.timezone.label')}
          </label>
          <select
            id="timezone_id"
            name="timezone_id"
            className="form-select"
            value={formData.timezone_id}
            onChange={(e) => handleChange(e, 'timezone_id')}
          >
            <option value="">{t('tenants.form.pageTexts.fields.timezone.placeholder')}</option>
            {timezones.map((tz: any) => (
              <option key={tz.id} value={tz.id}>
                {tz.tz_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="category_id">
            {t('tenants.form.pageTexts.fields.category.label')}
          </label>
          <select
            id="category_id"
            name="category_id"
            className="form-select"
            value={formData.category_id}
            onChange={(e) => handleChange(e, 'category_id')}
          >
            <option value="">{t('tenants.form.pageTexts.fields.category.placeholder')}</option>
            {categories.map((category: any) => (
              <option key={category.id} value={category.id}>
                {category.category_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <Input
            id="subscription_start_date"
            name="subscription_start_date"
            type="date"
            label={t('tenants.form.pageTexts.fields.startDate.label')}
            required
            value={formData.subscription_start_date}
            error={!!errors.subscription_start_date}
            errorMessage={errors.subscription_start_date}
            onChange={handleChange}
          />
        </div>
        {edit && (
          <div className="col-md-4">
            <label className="form-label d-block">{t('tenants.form.pageTexts.fields.status.label')}</label>
            <input className="form-control" value={t('tenants.form.pageTexts.fields.status.controlledNotice')} disabled readOnly />
          </div>
        )}
        {!edit && (
          <div className="col-md-4">
            <label className="form-label d-block">{t('tenants.form.pageTexts.fields.status.label')}</label>
            <input className="form-control" value={t('tenants.form.pageTexts.fields.status.lockedProvisioning')} disabled readOnly />
          </div>
        )}

        <div className="col-md-8">
          <Input
            id="address_notes"
            name="address_notes"
            label={t('tenants.form.pageTexts.fields.addressNotes.label')}
            value={formData.address_notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('tenants.form.pageTexts.buttons.cancel')}
            onClick={handleCancelClick}
          />
          <Button type="submit" className="btn btn-primary" text={t('tenants.form.pageTexts.buttons.save')} loading={loading} />
        </div>
      </form>

      <Modal
        open={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        title={t('tenants.form.pageTexts.unsavedGuard.title')}
        content={t('tenants.form.pageTexts.unsavedGuard.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('tenants.form.pageTexts.unsavedGuard.stay')}
              onClick={() => setShowLeaveConfirm(false)}
            />
            <Button
              className="btn btn-danger"
              text={t('tenants.form.pageTexts.unsavedGuard.leave')}
              onClick={() => {
                setShowLeaveConfirm(false);
                goToList();
              }}
            />
          </>
        }
      />
    </div>
  );
};

export default TenantForm;
